import DynamicField from './DynamicField';

/**
 * Renders a flat dynamic-fields array (workflow-step or nationality fields) as an answer
 * form. A field with `depends_on: { field_id, value }` only appears once its parent has been
 * answered with that option — and it's rendered nested under the parent instead of as its
 * own grid cell, so the surrounding layout doesn't need to know about the nesting.
 */

function isConditionMet(dependsOn, parentAnswer) {
  const selected = Array.isArray(parentAnswer) ? parentAnswer : [parentAnswer];
  return selected.includes(dependsOn.value);
}

function FieldNode({ field, fields, answers, onAnswer, errorFor, depth }) {
  const children = fields.filter((f) => f.depends_on?.field_id === field.id);
  const answer = answers[field.id];

  return (
    <div className={depth ? 'mt-2 space-y-2 border-l-2 border-gray-200 pl-3' : 'space-y-2'}>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          {field.label}
          {field.validation?.required && <span className="text-red-500"> *</span>}
        </label>
        <DynamicField field={field} value={answer} onChange={(value) => onAnswer(field.id, value)} />
        {errorFor(field.id) && <p className="mt-1 text-xs text-red-600">{errorFor(field.id)}</p>}
      </div>
      {children
        .filter((child) => isConditionMet(child.depends_on, answer))
        .map((child) => (
          <FieldNode key={child.id} field={child} fields={fields} answers={answers} onAnswer={onAnswer} errorFor={errorFor} depth={depth + 1} />
        ))}
    </div>
  );
}

export default function FieldAnswerList({ fields, answers, onAnswer, errorFor = () => null, className }) {
  const topLevel = fields.filter((f) => !f.depends_on);

  return (
    <div className={className}>
      {topLevel.map((f) => (
        <FieldNode key={f.id} field={f} fields={fields} answers={answers} onAnswer={onAnswer} errorFor={errorFor} depth={0} />
      ))}
    </div>
  );
}
